import React, { useEffect, useState } from 'react';
import { StyleSheet, View, Text, ActivityIndicator, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import missionService from '../../services/missionService';
import MissionPointsList from './MissionPointsList';
import SimpleMapView from './SimpleMapView';

/**
 * Componente que carga y muestra los puntos de una misión
 * @param {Object} props - Propiedades del componente
 * @param {number} props.missionId - ID de la misión
 */
const MissionPointsSection = ({ missionId }) => {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('map'); // 'map' o 'list'
  const [key, setKey] = useState(0); // Clave para forzar el re-renderizado del mapa

  // Cargar los puntos de la misión
  useEffect(() => {
    const loadPoints = async () => {
      if (!missionId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const response = await missionService.getMissionPoints(missionId);
        
        // Extraer los puntos del formato GeoJSON
        let missionPoints = [];
        if (response && response.type === 'FeatureCollection' && Array.isArray(response.features)) {
          // Convertir los features GeoJSON a un formato más simple para el componente
          missionPoints = response.features.map(feature => ({
            id: feature.id,
            order: feature.properties.order,
            name: feature.properties.name,
            description: feature.properties.description,
            altitude: feature.properties.altitude,
            estimated_time: feature.properties.estimated_time,
            location: feature.geometry
          }));
        } else if (Array.isArray(response)) {
          missionPoints = response;
        }
        
        console.log('Puntos de misión procesados:', missionPoints);
        setPoints(missionPoints);
        
        // Forzar re-renderizado del mapa
        setKey(prevKey => prevKey + 1);
      } catch (error) {
        console.error('Error al cargar los puntos de la misión:', error);
        setError('No se pudieron cargar los puntos de la misión');
      } finally {
        setLoading(false);
      }
    };
    
    loadPoints();
  }, [missionId]);

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="small" color="#3498db" />
        <Text style={styles.loadingText}>Cargando puntos...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.centered}>
        <Text style={styles.errorText}>{error}</Text>
      </View>
    );
  }

  if (!points || points.length === 0) {
    return (
      <View style={styles.centered}>
        <Text style={styles.emptyText}>No hay puntos definidos para esta misión</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.pointsCount}>Total: {points.length} puntos</Text>
        <View style={styles.viewToggle}>
          <TouchableOpacity 
            style={[styles.viewToggleButton, viewMode === 'map' && styles.viewToggleButtonActive]} 
            onPress={() => setViewMode('map')}
          >
            <Ionicons name="map-outline" size={18} color={viewMode === 'map' ? '#fff' : '#3498db'} />
            <Text style={[styles.viewToggleText, viewMode === 'map' && styles.viewToggleTextActive]}>Mapa</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={[styles.viewToggleButton, viewMode === 'list' && styles.viewToggleButtonActive]} 
            onPress={() => setViewMode('list')}
          >
            <Ionicons name="list-outline" size={18} color={viewMode === 'list' ? '#fff' : '#3498db'} />
            <Text style={[styles.viewToggleText, viewMode === 'list' && styles.viewToggleTextActive]}>Lista</Text>
          </TouchableOpacity>
        </View>
      </View>
      
      {viewMode === 'map' ? (
        <SimpleMapView key={`map-${missionId}-${key}`} points={points} />
      ) : (
        <MissionPointsList points={points} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 10,
    color: '#7f8c8d',
    fontSize: 14,
  },
  errorText: {
    color: '#e74c3c',
    fontSize: 14,
    textAlign: 'center',
  },
  emptyText: {
    color: '#7f8c8d',
    fontSize: 14,
    fontStyle: 'italic',
    textAlign: 'center',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  pointsCount: {
    fontSize: 14,
    color: '#7f8c8d',
  },
  viewToggle: {
    flexDirection: 'row',
    borderRadius: 5,
    borderWidth: 1,
    borderColor: '#3498db',
    overflow: 'hidden',
  },
  viewToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  viewToggleButtonActive: {
    backgroundColor: '#3498db',
  },
  viewToggleText: {
    fontSize: 12,
    color: '#3498db',
    marginLeft: 5,
  },
  viewToggleTextActive: {
    color: '#fff',
  },
});

export default MissionPointsSection;